import { motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import type { ScreenWinner } from '../types'
import { Garland, GoldenRain } from './festive'
import { CandidatePhoto } from './shared'

/** Tailles selon le nombre de titres à l'écran : un seul s'affiche en géant. */
const SIZES = {
  1: { label: 'min(6.4vh,3.8vw)', number: 'min(6vh,3.6vw)', name: 'min(12vh,7vw)', swags: 6 },
  2: { label: 'min(5.2vh,2.9vw)', number: 'min(5vh,2.9vw)', name: 'min(9vh,4.4vw)', swags: 5 },
  3: { label: 'min(4vh,1.9vw)', number: 'min(4vh,2vw)', name: 'min(7vh,2.9vw)', swags: 4 },
} as const

/**
 * La proclamation, à la télé : « Vainqueur · Miss », la photo, le numéro et
 * le nom — en très grand, sous une guirlande, une carte par titre.
 *
 * Tous les titres proclamés tiennent sur une seule page, côte à côte (un,
 * deux ou trois) : la salle ne doit pas attendre qu'un carrousel revienne à
 * Master pour applaudir. Aucun chiffre ici — les scores n'existent pour la
 * salle qu'une fois publiés ; ils remplacent alors cette vue, le palmarès
 * restant en tête de page.
 */
export function WinnersCelebration({
  winners,
  eventName,
}: {
  winners: ScreenWinner[]
  eventName: string
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const count = Math.min(Math.max(winners.length, 1), 3) as 1 | 2 | 3
  const sizes = SIZES[count]

  return (
    <section
      aria-label={t('screen.winners')}
      className="relative flex h-full min-h-0 flex-col overflow-hidden"
    >
      <p className="relative shrink-0 truncate text-center text-[min(2.6vh,1.6vw)] uppercase tracking-[0.3em] text-content-secondary">
        {eventName}
      </p>
      <ul
        className="relative mt-[2vh] grid min-h-0 flex-1 justify-center gap-[2.5vw]"
        style={{
          gridTemplateColumns: `repeat(${winners.length}, minmax(0, ${count === 1 ? '64vw' : '1fr'}))`,
        }}
      >
        {winners.map((winner, index) => (
          <motion.li
            key={winner.competition_category_id ?? winner.contest}
            className="flex min-h-0 min-w-0 flex-col items-center overflow-hidden rounded-2xl border-2 border-[color-mix(in_srgb,var(--champagne-400)_70%,transparent)] bg-bg-elevated text-center"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 + index * 0.35 }}
          >
            <Garland swags={sizes.swags} className="shrink-0" />
            <p
              className="mt-[0.5vh] w-full shrink-0 truncate px-[2vw] font-display uppercase leading-tight tracking-[0.08em] text-champagne-300"
              style={{ fontSize: sizes.label }}
            >
              {t('screen.winnerOf', { contest: winner.contest })}
            </p>
            {/* La photo prend toute la hauteur qui reste, au format portrait :
                c'est elle que la salle regarde pendant les applaudissements. */}
            <div className="relative mt-[1.8vh] min-h-0 w-full flex-1">
              <div className="absolute inset-y-0 left-1/2 aspect-[3/4] h-full max-w-[90%] -translate-x-1/2 overflow-hidden rounded-xl border-4 border-champagne-300 shadow-[0_0_6vh_rgba(235,220,184,0.25)]">
                <CandidatePhoto url={winner.photo_url ?? null} number={winner.number ?? null} name={winner.name} />
              </div>
            </div>
            <div className="w-full shrink-0 px-[2vw] pb-[2.5vh] pt-[1.6vh]">
              {winner.number !== null && winner.number !== undefined ? (
                <p
                  className="font-mono font-semibold leading-none text-champagne-300"
                  style={{ fontSize: sizes.number }}
                >
                  {t('screen.number', { number: winner.number })}
                </p>
              ) : null}
              <p
                className="mt-[1vh] line-clamp-2 break-words font-display leading-[1.02] text-content-primary"
                style={{ fontSize: sizes.name }}
              >
                {winner.name}
              </p>
            </div>
          </motion.li>
        ))}
      </ul>
      {/* Par-dessus les cartes : des cartes pleine hauteur ne laissent
          presque rien voir d'une pluie posée derrière. Peu de paillettes,
          petites — la fête, sans cacher un visage. */}
      <GoldenRain
        seed={winners.map((winner) => winner.candidate_id ?? winner.name).join('|')}
        count={30}
      />
    </section>
  )
}
